<template>
  <div
    v-if="show"
    aria-live="assertive"
    class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
  >
    <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
      <Transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="notification.type === NOTIFICATION_TYPES.INFO"
          class="pointer-events-auto z-99 flex w-full max-w-md divide-x divide-secondary-200 rounded-lg border-secondary-200 bg-default shadow-lg ring-1 ring-secondary-300"
        >
          <div class="w-0 flex-1 p-4">
            <div class="flex items-start gap-2">
              <div class="shrink-0 pt-1" v-if="notification.avatarLiteralName">
                <BaseAvatar :name="notification.avatarLiteralName" :size="10" />
              </div>
              <div
                class="flex-1"
                v-if="notification.title || notification.message"
              >
                <p
                  class="text-sm font-semibold text-secondary-900"
                  v-html="notification.title"
                />
                <p
                  class="mt-1 text-sm text-secondary-500 line-clamp-1 break-words"
                  v-html="notification.message"
                />
              </div>
            </div>
          </div>
          <div class="flex">
            <div class="flex flex-col divide-y divide-secondary-200">
              <div class="flex h-0 flex-1">
                <BaseButton
                  @click="onClick"
                  class="focus:ring-secondary-500 flex w-full items-center justify-center rounded-none rounded-tr-lg border border-transparent px-4 py-3 text-sm font-medium text-primary-600 hover:text-primary-500 focus:z-10 focus:outline-none focus:ring-2"
                  label="Reply"
                />
              </div>
              <div class="flex h-0 flex-1">
                <BaseButton
                  @click="closePopup"
                  class="flex w-full items-center justify-center rounded-none rounded-br-lg border border-transparent px-4 py-3 text-sm font-medium text-secondary-700 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  label="Dismiss"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="notification.type === NOTIFICATION_TYPES.ERROR"
          class="pointer-events-auto flex w-full max-w-md rounded-lg border-red-500 bg-red-700 shadow-lg ring-1 ring-secondary-300 ring-opacity-5"
        >
          <div
            class="flex flex-1 p-4 items-center space-x-4 cursor-pointer"
            @click="onClick"
          >
            <ExclamationTriangleIcon class="h-10 w-10 text-default" />
            <div class="flex items-start text-default">
              <div
                v-if="notification.title || notification.message"
                class="flex-1"
              >
                <p class="text-sm font-semibold" v-html="notification.title" />
                <p
                  class="mt-1 text-sm line-clamp-1 break-words"
                  v-html="notification.message"
                />
              </div>
            </div>
          </div>

          <div class="flex p-2" @click="closePopup">
            <XMarkIcon class="h-5 w-5 text-default cursor-pointer" />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Notifications, NOTIFICATION_TYPES } from "@/models/notification";
import { inject, onMounted, ref } from "vue";
import useAudio, { AUDIOS } from "@/composables/useAudio";
import BaseButton from "./control/base-button.vue";

const show = ref(false);
const notification = ref<Notifications>({
  avatarLiteralName: "",
  title: "",
  type: NOTIFICATION_TYPES.INFO,
  message: "",
  data: {},
  show: false,
});
const emitter: any = inject("emitter");
const { playAudio, pauseAudio } = useAudio();

const closePopup = () => {
  show.value = false;
};

const onClick = () => {
  emitter.emit("notification-clicked", notification.value);
  show.value = false;
};

onMounted(() => {
  emitter.on("show-notification", (data: any) => {
    show.value = true;
    notification.value = data;

    playSound(notification.value.type);

    setTimeout(() => {
      show.value = false;
    }, 30000);
  });

  emitter.on("close-notification", () => {
    show.value = false;
    notification.value = {
      avatarLiteralName: "",
      title: "",
      type: NOTIFICATION_TYPES.INFO,
      message: "",
      data: {},
      show: false,
    };
    pauseAudio();
  });

  emitter.on("stop-sound", () => {
    pauseAudio();
  });

  const playSound = (type: string) => {
    if (type == NOTIFICATION_TYPES.INFO) {
      playAudio(AUDIOS.NOTIFICATION);
    } else if (type == NOTIFICATION_TYPES.ERROR) {
      playAudio(AUDIOS.SOS_ALARM, true);
    }
  };

  emitter.on("play-sound", (notificationType: string) => {
    playSound(notificationType);
  });
});
</script>
